import { Chart as ChartJS, Scale } from "chart.js/auto";
import { useEffect, useRef, useState } from "react";
import Navigator from "../../models/navigator";
import Navigable, {
  NavigatorMove,
  SetFilters,
  SetTableFilters,
} from "../../models/navigable";
import Filter from "../../models/filter";
import LoadingIndicatorLargeCenter from "../common/LoadingIndicatorLargeCenter";
import ProjectInfo from "../../models/projectInfo";
import { Chart as ChartModel } from "../../models/chart";
import colorHelper from "../../util/colorHelper";
import NavigatorPosition from "../../models/navigatorPosition";
import RoadLocatorSegment from "../../models/roadLocatorSegment";
import interpolation from "../../util/interpolation";
import unitConverter from "../../util/units/unitConverter";
import TableColum from "../../models/tableColumn";
import Unit from "../../util/units/unit";
import numberHelper from "../../util/numberHelper";
import ChartSeriesType from "../../models/chartSeriesType";
import zoomPlugin from "chartjs-plugin-zoom";
import Tooltip from "../common/Tooltip";
import ZoomFit from "../../assets/images/zoom_fit.png";
import filterHelper from "../../models/filterHelper";
import FilterIcon from "@mui/icons-material/FilterAlt";
import Settings from "../../models/settings";
import TableColumnDataType from "../../models/tableColumnDataType";

const Chart: React.FC<{
  chart: ChartModel;
  tableColumns: TableColum[];
  navigator: Navigator;
  project: ProjectInfo;
  settings: Settings;
}> = ({ chart: chartModel, tableColumns, navigator, project, settings }) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [title, setTitle] = useState<string>("");
  const [isBusy, setIsBusy] = useState<boolean>(false);

  const [chart, setChart] = useState<ChartJS>();

  const [tableFiltersExt, setTableFiltersExt] = useState<Filter[]>([]);

  useEffect(() => {
    if (!canvasRef.current) {
      return;
    }

    let useUnitConversionOnX = true;
    const useUnitConversionOnY = true;

    const onEnableChartsUnitsOnX = (checked: boolean) => {
      useUnitConversionOnX = checked;

      if (canvasRef.current) {
        init(canvasRef.current);
      }
    };

    settings.enableChartsUnitsOnXObservable.attach(onEnableChartsUnitsOnX);

    let navigatorCurrentLocatorData: RoadLocatorSegment[] = [];
    let navigatorPrevLocatorData: RoadLocatorSegment[] = [];

    let currentLocatorDataFull: RoadLocatorSegment[] = [];
    let currentLocatorData: RoadLocatorSegment[] = []; // filtered

    let tableFilters: Filter[] = [];

    const navigable = new Navigable();
    navigable.id = `chart[${chartModel.chartName}]`;
    //navigable.id = `chart[${chartModel.chartName}${Math.floor(Math.random() * 10)}]`;

    setTitle(chartModel.chartName);

    let chart: ChartJS | undefined;
    let isDisposed = false;

    const gapStartsMap: Map<string, Set<number>> = new Map();

    const getFieldData = (
      fieldName: string,
      currentLocatorData: RoadLocatorSegment[],
    ) => {
      let fieldDataItems = currentLocatorData.map((d) =>
        d.dataItems[fieldName] !== null ? Number(d.dataItems[fieldName]) : null,
      );

      let gapStarts = gapStartsMap.get(fieldName);

      if (!gapStarts) {
        gapStartsMap.set(fieldName, new Set<number>());
        gapStarts = gapStartsMap.get(fieldName);
      }

      gapStarts?.clear();

      fieldDataItems = fieldDataItems.map((v, i) => {
        if (v == null && i > 0) {
          gapStarts?.add(i);
          return fieldDataItems[i - 1];
        }

        return v;
      });

      return fieldDataItems;
    };

    let currentPosition = -1;

    const getInterpolatedPixelForValue = (scale: Scale, value: number) => {
      const nums = scale.getLabels().map(Number);

      if (nums.length < 2) {
        return -1;
      }

      let isAscending = nums[0] <= nums[1];

      for (let i = 0; i < nums.length - 1; i++) {
        const leftValue = nums[i];
        const rightValue = nums[i + 1];

        let found = false;

        if (isAscending) {
          if (value >= leftValue && value <= rightValue) {
            found = true;
          }
        } else {
          if (value <= leftValue && value >= rightValue) {
            found = true;
          }
        }

        if (found) {
          const leftPixel = scale.getPixelForValue(i);
          const rightPixel = scale.getPixelForValue(i + 1);

          // const valuePixel = interpolation.linearInterpolation(
          //   leftValue,
          //   leftPixel,
          //   rightValue,
          //   rightPixel,
          //   value,
          // );

          // return valuePixel;

          const t = (value - leftValue) / (rightValue - leftValue);

          return leftPixel + (rightPixel - leftPixel) * t;
        }
      }

      const lastValue = nums[nums.length - 1];
      if (value > lastValue) {
        return scale.getPixelForValue(nums.length - 1);
      }

      return scale.getPixelForValue(1);
    };

    const getInterpolatedValueForPixel = (scale: Scale, pixel: number) => {
      const nums = scale.getLabels().map(Number);

      if (nums.length < 2) {
        return -1;
      }

      let isAscending = nums[0] <= nums[1];

      for (let i = 0; i < nums.length - 1; i++) {
        const leftPixel = scale.getPixelForValue(i);
        const rightPixel = scale.getPixelForValue(i + 1);

        let found = false;

        if (isAscending) {
          if (
            pixel >= Math.min(leftPixel, rightPixel) &&
            pixel <= Math.max(leftPixel, rightPixel)
          ) {
            found = true;
          }
        } else {
          if (
            pixel <= Math.max(leftPixel, rightPixel) &&
            pixel >= Math.min(leftPixel, rightPixel)
          ) {
            found = true;
          }
        }

        if (found) {
          const t = (pixel - leftPixel) / (rightPixel - leftPixel);

          const leftValue = nums[i];
          const rightValue = nums[i + 1];

          return leftValue + (rightValue - leftValue) * t;
        }
      }

      const lastPixel = scale.getPixelForValue(nums.length - 1);
      if (pixel > lastPixel) {
        return nums[nums.length - 1];
      }

      return nums[0];
    };

    const positionLinePlugin = {
      id: "positionLine",

      afterDatasetsDraw(chart: ChartJS, args: any, pluginOptions: any) {
        const { ctx, chartArea, scales } = chart;

        const xScale = scales.x;

        const x = getInterpolatedPixelForValue(xScale, currentPosition);

        ctx.save();

        ctx.beginPath();
        ctx.moveTo(x, chartArea.top);
        ctx.lineTo(x, chartArea.bottom);

        ctx.lineWidth = 2;
        ctx.strokeStyle = "red";
        ctx.stroke();

        ctx.restore();
      },
    };

    const onChartClickX = (xValue: number) => {
      // console.log("onChartClickXValue", xValue);

      let isUpchainage = true;
      let roadLocatorSegment = undefined;

      if (currentLocatorData.length > 0) {
        isUpchainage = currentLocatorData[0].isUpChainage;
      }

      if (isUpchainage) {
        roadLocatorSegment = currentLocatorData.find(
          (s) => xValue >= s.startChainage && xValue <= s.endChainage,
        );
      } else {
        roadLocatorSegment = currentLocatorData.find(
          (s) => xValue <= s.startChainage && xValue >= s.endChainage,
        );
      }

      if (!roadLocatorSegment) {
        // if (isUpChainage) {
        //      startChaiange - we use startChaiange
        //   s1 0   .. 100
        //   s2 100 .. 200 // 300
        //   s3 400 .. 500
        // } else {
        //      startChaiange - we use startChaiange
        //   s1 500 .. 400 // 300
        //   s2 200 .. 100
        //   s3 100 .. 0

        for (let i = 0; i < currentLocatorData.length; i++) {
          const s = currentLocatorData[i];

          if (isUpchainage) {
            if (s.startChainage > xValue) {
              roadLocatorSegment = s;
              xValue = s.startChainage;
              break;
            }
          } else {
            if (s.startChainage < xValue) {
              roadLocatorSegment = s;
              xValue = s.startChainage;
              break;
            }
          }
        }
      }

      if (roadLocatorSegment) {
        const chainage = xValue;
        const distance = interpolation.linearInterpolation(
          roadLocatorSegment.startChainage,
          roadLocatorSegment.startDistance,
          roadLocatorSegment.endChainage,
          roadLocatorSegment.endDistance,
          chainage,
        );

        const navigatorPosition = new NavigatorPosition();
        navigatorPosition.idProject = roadLocatorSegment.idProject;
        navigatorPosition.idRoad = roadLocatorSegment.idRoad;
        navigatorPosition.idSession = roadLocatorSegment.idSession;
        navigatorPosition.distance = distance;
        navigatorPosition.chainage = chainage;

        navigator.move(navigatorPosition);
      }
    };

    const guideLinePlugin = {
      id: "guideLine",

      afterEvent(chart: any, args: any) {
        const event = args.event;

        if (event.type === "mousemove") {
          chart.guideLine = {
            x: event.x,
            y: event.y,
          };

          // force redraw
          chart.draw();
        }

        if (event.type === "mouseout") {
          chart.guideLine = null;
          chart.draw();
        }

        if (event.type === "click") {
          const { left, right, top, bottom } = chart.chartArea;

          const insideChart =
            event.x >= left &&
            event.x <= right &&
            event.y >= top &&
            event.y <= bottom;

          if (insideChart) {
            const xScale = chart.scales.x;
            const xValue = getInterpolatedValueForPixel(xScale, event.x);
            onChartClickX(xValue);
          }
        }
      },

      afterDraw(chart: any) {
        if (!chart.guideLine) return;

        const {
          ctx,
          chartArea: { left, right, top, bottom },
        } = chart;

        const { x, y } = chart.guideLine;

        if (x < left || x > right || y < top || y > bottom) {
          return;
        }

        ctx.save();

        ctx.lineWidth = 1;
        ctx.strokeStyle = "rgba(0,0,0,0.5)";

        // horizontal line
        ctx.beginPath();
        ctx.moveTo(left, y);
        ctx.lineTo(right, y);
        ctx.stroke();

        // vertical line
        ctx.beginPath();
        ctx.moveTo(x, top);
        ctx.lineTo(x, bottom);
        ctx.stroke();

        ctx.restore();
      },
    };

    const init = (canvas: HTMLCanvasElement) => {
      if (isDisposed) {
        return;
      }

      let xLabels = currentLocatorData.map((d) => d.startChainage);

      let xDisplayLabels: string[] = [];
      if (!chartModel.useDistanceOnXAxis) {
        xDisplayLabels = currentLocatorData.map(
          (d) => d.dataItems[chartModel.xAxisFieldName],
        );
      }

      const getFieldConvertedValue = (
        fieldName: string | undefined,
        fieldValue: number | string,
      ): number | string | undefined => {
        const tableColumn = tableColumns.find(
          (c) => c.columnName === fieldName,
        );

        if (!tableColumn) {
          return fieldValue;
        }

        if (tableColumn.dataType === TableColumnDataType.Number) {
          let convertedValue: number | undefined = Number(fieldValue);

          if (tableColumn.unitMeasure !== Unit.None) {
            convertedValue = unitConverter.convertValue(
              Number(fieldValue),
              tableColumn.unitMeasure,
            );
          }

          if (tableColumn.decimals !== -1) {
            convertedValue = numberHelper.numberToNDigits(
              convertedValue,
              tableColumn.decimals,
            );
          }

          return convertedValue;
        }

        return fieldValue;
      };

      const getFieldUnit = (fieldName: string | undefined): string => {
        const tableColumn = tableColumns.find(
          (c) => c.columnName === fieldName,
        );

        let unit = "";

        if (tableColumn && tableColumn.unitMeasure !== Unit.None) {
          unit = unitConverter.getSymbol(tableColumn.unitMeasure);
        }

        return unit;
      };

      const buildDataset = (
        type: string,
        label: string,
        fieldName: string,
        color: string,
      ) => {
        let seriesType = "";

        switch (type) {
          case ChartSeriesType.Area:
            seriesType = "line";
            break;

          case ChartSeriesType.Bar:
            seriesType = "bar";
            break;

          case ChartSeriesType.Line:
            seriesType = "line";
            break;

          case ChartSeriesType.Point:
            seriesType = "scatter";
            break;

          default:
            break;
        }

        const isFill = type === ChartSeriesType.Area;

        return {
          tag: fieldName,
          type: seriesType,
          label: label,
          data: getFieldData(fieldName, currentLocatorData),
          stepped: true,
          borderColor: color,
          backgroundColor: colorHelper.hexToRgba(color, 0.7),
          fill: isFill,
          segment: {
            borderColor(ctx: any) {
              const color = ctx.p0.$context.dataset.borderColor;

              let gapStarts = gapStartsMap.get(fieldName);

              if (gapStarts?.has(ctx.p0DataIndex)) {
                return "transparent";
              }

              return color;
            },

            backgroundColor(ctx: any) {
              const color = ctx.p0.$context.dataset.backgroundColor;

              let gapStarts = gapStartsMap.get(fieldName);

              if (gapStarts?.has(ctx.p0DataIndex)) {
                return "transparent";
              }

              return color;
            },
          },
        };
      };

      const datasets: any[] = [];

      for (const chartSeries of chartModel.chartSeries) {
        const dataset = buildDataset(
          chartSeries.seriesType,
          chartSeries.seriesName,
          chartSeries.fieldName,
          chartSeries.seriesColor,
        );
        datasets.push(dataset);
      }

      // const xAxisName = "Chainage";
      // const yAxisName = "IRI";

      const xAxisName = chartModel.xAxisName;
      const yAxisName = chartModel.yAxisName;

      chart?.destroy();

      chart = new ChartJS(canvas, {
        data: {
          labels: xLabels, // x (union distinct of all x from all datasets)
          datasets,
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: {
            duration: 1000,
          },

          transitions: {
            show: {
              animations: {
                visible: {
                  duration: 0,
                },
              },
            },

            hide: {
              animations: {
                visible: {
                  duration: 0,
                },
              },
            },
          },
          scales: {
            x: {
              type: "category",
              title: {
                display: true,
                text: xAxisName,
              },
              ticks: {
                autoSkip: true,
                maxTicksLimit: 15,
                maxRotation: 0,
                callback(tickValue) {
                  // note that this. here is the chart internal class, not this class

                  if (chartModel.useDistanceOnXAxis) {
                    let value = Number(
                      this.getLabelForValue(Number(tickValue)),
                    );

                    if (useUnitConversionOnX) {
                      value = unitConverter.metersToMiles(value);
                    }

                    return numberHelper.numberTo3Digits(value);
                  } else {
                    let value: number | string | undefined =
                      xDisplayLabels[Number(tickValue)];

                    if (value === null) {
                      return undefined;
                    }

                    if (useUnitConversionOnX) {
                      value = getFieldConvertedValue(
                        chartModel.xAxisFieldName,
                        value,
                      );
                    }

                    return value;
                  }
                },
              },
            },
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: yAxisName,
              },
              ticks: {
                callback(tickValue) {
                  if (this.chart.data.datasets.length === 0) {
                    return;
                  }

                  // note that this. here is the chart internal class, not this class
                  const fieldName = (this.chart.data.datasets[0] as any).tag;

                  let value: number | string | undefined = Number(tickValue);

                  if (useUnitConversionOnY) {
                    value = getFieldConvertedValue(fieldName, value);
                  }

                  return value;
                },
              },
            },
          },
          plugins: {
            tooltip: {
              callbacks: {
                title() {
                  return "";
                },

                label(context) {
                  // y

                  const yTitle = context.dataset.label;

                  const fieldName = (context.dataset as any).tag;

                  let value: number | string | undefined = Number(
                    context.parsed.y,
                  );

                  if (!useUnitConversionOnY) {
                    return `${yTitle}: ${value}`;
                  }

                  value = getFieldConvertedValue(fieldName, value);
                  const unit = getFieldUnit(fieldName);

                  return `${yTitle}: ${value} ${unit}`;
                },

                footer(items) {
                  // x

                  const chart = items[0].chart;

                  const xTitle =
                    (chart as any).options.scales.x.title?.text || "X";

                  if (chartModel.useDistanceOnXAxis) {
                    let value = Number(items[0].label);

                    if (!useUnitConversionOnX) {
                      return `${xTitle}: ${numberHelper.numberTo3Digits(value)}`;
                    }

                    value = unitConverter.metersToMiles(value);

                    const unit = "(mi)";

                    return `${xTitle}: ${numberHelper.numberTo3Digits(value)} ${unit}`;
                  } else {
                    let value: number | string | undefined =
                      xDisplayLabels[items[0].dataIndex];

                    if (value === null) {
                      return undefined;
                    }

                    if (!useUnitConversionOnX) {
                      return `${xTitle}: ${value}`;
                    }

                    value = getFieldConvertedValue(
                      chartModel.xAxisFieldName,
                      value,
                    );

                    const unit = getFieldUnit(chartModel.xAxisFieldName);

                    return `${xTitle}: ${value} ${unit}`;
                  }
                },
              },
            },
            zoom: {
              pan: {
                enabled: true,
                mode: "x",
              },
              zoom: {
                wheel: {
                  enabled: true,
                },
                pinch: {
                  enabled: true,
                },
                drag: {
                  enabled: false,
                  backgroundColor: "rgba(54, 162, 235, 0.2)",
                },
                mode: "x",
              },
            },
          },
        },
        plugins: [positionLinePlugin, guideLinePlugin, zoomPlugin],
      });

      setChart(chart);
    };

    const navigatorMove: NavigatorMove = async (
      navigatorPosition: NavigatorPosition,
    ) => {
      if (isDisposed) {
        return;
      }

      if (!canvasRef.current) {
        return;
      }

      navigatorPrevLocatorData = navigatorCurrentLocatorData;
      navigatorCurrentLocatorData = navigator.getCurrentLocatorData();

      const isNewData =
        navigatorCurrentLocatorData.length === 0 ||
        navigatorPrevLocatorData.length === 0 ||
        !navigatorCurrentLocatorData[0].isEqual(navigatorPrevLocatorData[0]) ||
        !navigatorCurrentLocatorData[
          navigatorCurrentLocatorData.length - 1
        ].isEqual(
          navigatorPrevLocatorData[navigatorPrevLocatorData.length - 1],
        );

      if (isNewData) {
        currentLocatorData = [...navigatorCurrentLocatorData];

        if (currentLocatorData.length > 0) {
          const hasAllBarSeries =
            chartModel.chartSeries.find(
              (cs) => cs.seriesType !== ChartSeriesType.Bar,
            ) === undefined;

          // for bar charts if we have 2 segments we should have 2 bars, not 3
          // so in this case we don't add the last segment that is generated with the end chainage of the segment in order to navigate the last segement

          if (!hasAllBarSeries) {
            // add the last segment that is generated with the end chainage of the segment in order to navigate the last segement

            const lastSegment =
              currentLocatorData[currentLocatorData.length - 1];

            // const isUpChainage = firstSegment.isUpChainage;
            // if (isUpChainage) {
            //      startChaiange - we use startChaiange
            //   s1 0   .. 100 v1
            //   s2 100 .. 200 v2
            //   s3 200 .. 300 v3
            //   s4 300 .. 300 v3 // add this to have endChaiange as end
            // } else {
            //      startChaiange - we use startChaiange
            //   s1 300 .. 200 v1
            //   s2 200 .. 100 v2
            //   s3 100 .. 0   v3
            //   s4 0   .. 0   v3
            // }

            const lastSegmentFix = structuredClone(lastSegment);
            lastSegmentFix.startChainage = lastSegmentFix.endChainage;
            lastSegmentFix.startDistance = lastSegmentFix.endDistance;
            currentLocatorData.push(lastSegmentFix);
          }
        }

        currentLocatorDataFull = [...currentLocatorData];

        filterCurrentLocatorData();

        init(canvasRef.current);
      }

      if (navigatorPosition.chainage !== undefined) {
        currentPosition = navigatorPosition.chainage;
        chart?.draw(); // redraw without changing data
      }
    };
    navigable.navigatorMove = navigatorMove;

    const setFilters: SetFilters = async (
      filters: Filter[],
      isProjectChanging: boolean,
    ) => {
      if (isDisposed) {
        return;
      }

      if (!canvasRef.current) {
        return;
      }

      setIsBusy(true);
      init(canvasRef.current);
      setIsBusy(false);
    };
    navigable.setFilters = setFilters;

    const setTableFilters: SetTableFilters = (filters: Filter[]) => {
      if (isDisposed) {
        return;
      }

      if (!canvasRef.current) {
        return;
      }

      tableFilters = filters;
      setTableFiltersExt(filters);

      filterCurrentLocatorData();

      setIsBusy(true);
      init(canvasRef.current);
      setIsBusy(false);
    };

    const filterCurrentLocatorData = () => {
      currentLocatorData = currentLocatorDataFull;

      if (tableFilters.length > 0) {
        const filteredCurrentLocatorData = [];
        for (const filter of tableFilters) {
          for (const currentLocatorDataItem of currentLocatorDataFull) {
            const fieldValue =
              currentLocatorDataItem.dataItems[filter.fieldName];
            const isInFilter = filterHelper.isFiledValueInFilter(
              fieldValue,
              filter,
            );
            if (isInFilter) {
              filteredCurrentLocatorData.push(currentLocatorDataItem);
            }
          }
        }
        currentLocatorData = filteredCurrentLocatorData;
      }
    };

    navigable.setTableFilters = setTableFilters;

    navigator.attach(navigable);

    // this sould be called on open/close window
    if (
      navigator.navigatorPosition &&
      navigator.navigatorPosition.idProject === project.idProject
    ) {
      // console.log(`camera[${chartModel.chartName}]:navigator:move()-init`);
      navigatorMove(navigator.navigatorPosition);
    }

    return () => {
      navigator.detach(navigable);
      isDisposed = true;
      chart?.destroy();
      setChart(undefined);
      settings.enableChartsUnitsOnXObservable.detach(onEnableChartsUnitsOnX);
    };
  }, [chartModel, tableColumns, navigator, project, settings]);

  const onResetZoom = async () => {
    chart?.resetZoom();
  };

  return (
    <div
      ref={chartContainerRef}
      className="camera-img-container d-flex flex-column align-items-center"
      style={{
        paddingTop: "30px",
        paddingRight: "5px",
        paddingBottom: "5px",
        paddingLeft: "5px",
      }}
    >
      <div
        className="d-flex"
        style={{ position: "absolute", top: "4px", left: "4px" }}
      >
        <div className="d-flex flex-column">
          <div>
            <Tooltip
              title="Reset Zoom"
              placement="right"
              parentContainer={chartContainerRef}
            >
              <div
                className="image-button"
                style={{ marginBottom: "4px" }}
                onClick={onResetZoom}
              >
                <img style={{ height: "18px" }} src={ZoomFit} alt="" />
              </div>
            </Tooltip>
          </div>
        </div>
        {tableFiltersExt.length > 0 && (
          <div
            style={{ color: "#D0021B" }}
            title={"Filtered by " + filterHelper.filtersToText(tableFiltersExt)}
          >
            <FilterIcon />
          </div>
        )}
      </div>
      <div style={{ position: "absolute", top: "4px", fontWeight: "bold" }}>
        {`${title}`}
      </div>
      {isBusy && <LoadingIndicatorLargeCenter />}
      <canvas ref={canvasRef} />
    </div>
  );
};

export default Chart;
